import { Error as ErrorIcon, Lock } from '@mui/icons-material';
import { Chip, Grid } from '@mui/material';
import { HexColor } from '@nx-smartmonkey/shared/domain';
import { addSeconds, dateFormatShortTime, parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import { memo, MouseEvent, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { StopsIcon } from '../../../../../../../assets/svg/Plan/MenuBar/StopsIcon';
import { RightArrowIcon } from '../../../../../../../assets/svg/RightArrowIcon';
import { numericToText } from '../../../../../../../components/form-fields/FormTimeWindows';
import { getRouteColor } from '../../../../../../../constants/Colors';
import { Driver } from '../../../../../../../domain/driver/Driver';
import { useRetrieveActiveProject } from '../../../../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import Text from '../../../../../../../react-components/Text';
import { driverTimewindowIsInvalid } from '../../../../components/BrokenConstraintsTooltip';
import { DriverRouteProgress } from '../../../../components/DriverRouteProgress';
import ProblemsTooltip from '../../../../components/ProblemsTooltip';
import { DriverColorBar } from '../../../../components/StatusBar';
import { useRetrieveActivePlanDrivers } from '../../../../hooks/plan-details/useRetrieveActivePlanDrivers';
import { PlanDriversActionsMenu } from '../../../../menus/DriversActionsMenu';
import { TREE_ITEM_HEIGHT } from '../PlanOverviewListStyles';

const ConstraintChip = styled(Chip)`
  height: 16px !important;
  border-radius: 2px !important;
  padding: 2px 6px !important;
  margin-right: 4px !important;
  & .MuiChip-label {
    padding: 0 !important;
  }
`;

const ProgressConstraintChip = styled(ConstraintChip)<{ progressPercentage: number }>`
  border-bottom: 1px solid !important;
  border-image-slice: 1 !important;
  border-image-source: linear-gradient(
    to right,
    ${(props) => (props.progressPercentage > 100 ? RoutalPalette.error.medium : RoutalPalette.success.medium)}
      ${(props) => props.progressPercentage}%,
    transparent ${(props) => props.progressPercentage}% ${(props) => 100 - props.progressPercentage}%
  ) !important;

  & .MuiChip-label {
    padding-top: 1px !important;
  }
`;

const DriverChipContainer = styled(Grid)<{ isFirstInLevel: boolean }>`
  height: ${TREE_ITEM_HEIGHT}px;
  background-color: transparent;
  border-left: 1px solid ${RoutalPalette.neutral10};
  padding: 3px 10px 3px 4px;
  position: relative;
  cursor: pointer;
  & *: cursor: pointer;
`;

interface DriverChipProps {
  driver: Driver;
  isFirstInLevel: boolean;
  isExpanded: boolean;
  toggleExpanded: () => void;
}

export const DriverChip = memo(({ driver, isFirstInLevel, isExpanded, toggleExpanded }: DriverChipProps) => {
  const { setRightPanelInfo } = useRetrieveRightPanelInfo();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();

  const [contextAnchorDriverChip, setContextAnchorDriverChip] = useState<
    | {
        screenPosition: {
          top: number;
          left: number;
        };
      }
    | undefined
  >(undefined);

  const { data: activeProject } = useRetrieveActiveProject();

  const estimated_last_stop_time =
    driver.stops.length > 0 ? driver.stops[driver.stops.length - 1].estimated_arrival_time : null;

  const driverWeight = useMemo(() => {
    const { max_weight } = driver;
    const weight = driver.stops.reduce((acc, stop) => acc + (stop.weight || 0), 0);
    return { percentage: Math.floor((weight / (max_weight || 0)) * 100), total: weight };
  }, [driver]);

  const driverVolume = useMemo(() => {
    const { max_volume } = driver;
    const volume = driver.stops.reduce((acc, stop) => acc + (stop.volume || 0), 0);
    return { percentage: Math.floor((volume / (max_volume || 0)) * 100), total: volume };
  }, [driver]);

  const getTimeWindowsPercent = () => {
    const { time_window } = driver;
    if (!time_window) {
      return 0;
    }
    const [start, end] = time_window;
    const estimated = end - start;
    const used = (driver.planned_end_time || 0) - (driver.planned_start_time || 0);
    return Math.floor((used / estimated) * 100);
  };

  const renderLock = () => {
    if (!driver.is_locked) {
      return null;
    }
    return (
      <Grid
        item
        style={{
          position: `absolute`,
          left: `0px`,
          top: `0px`,
          width: `1rem`,
          height: `1rem`,
        }}
      >
        <Lock
          style={{
            fontSize: `14px`,
            color: RoutalPalette.secondary40,
            backgroundColor: RoutalPalette.oldWhite,
            border: `1px solid ${RoutalPalette.oldWhite}`,
            borderRadius: `50%`,
          }}
        />
      </Grid>
    );
  };

  const renderHours = () => {
    const { status, planned_start_time, planned_end_time, start_date, end_date, stops, estimated_end_time } = driver;
    const isNotStarted =
      status === `not_started` &&
      (planned_start_time || planned_start_time === 0) &&
      (planned_end_time || planned_end_time === 0);
    const isInTransit = status === `in_transit` && start_date && stops.length > 0;
    const isFinished = status === `finished` && end_date;

    let text = <div />;
    if (isNotStarted) {
      // Calculate the times
      const formattedStartTime = planned_start_time !== undefined && parseSecondsToHM(planned_start_time);
      const formattedEndTime = planned_end_time !== undefined && parseSecondsToHM(planned_end_time, true);

      text = (
        <>
          {formattedStartTime}
          <div style={{ margin: `0 2px`, userSelect: `none` }}>-</div>
          {formattedEndTime}
        </>
      );
    } else if (isInTransit) {
      // Calculate the times
      const formattedEstimatedEndTime =
        estimated_end_time ??
        estimated_last_stop_time ??
        (start_date &&
          planned_end_time !== undefined &&
          planned_start_time !== undefined &&
          addSeconds(new Date(start_date), planned_end_time - planned_start_time)) ??
        undefined;

      text = (
        <div
          style={{
            color: RoutalPalette.secondary00,
            fontWeight: 300,
            fontSize: `13px`,
            userSelect: `none`,
            display: `flex`,
          }}
        >
          {dateFormatShortTime(formattedEstimatedEndTime)}
        </div>
      );
    } else if (isFinished) {
      text = (
        <div style={{ color: RoutalPalette.markerStopColors.completed.medium, userSelect: `none`, display: `flex` }}>
          {dateFormatShortTime(end_date)}
        </div>
      );
    }

    return (
      <Grid
        style={{
          display: `flex`,
          alignItems: `center`,
        }}
      >
        {(isInTransit || isFinished) && (
          <Grid
            style={{
              height: `6px`,
              width: `6px`,
              backgroundColor: isInTransit
                ? RoutalPalette.routeStatusColors.in_transit.medium
                : RoutalPalette.routeStatusColors.finished.dark,
              marginRight: `4px`,
              borderRadius: `50%`,
            }}
          />
        )}
        <Text
          variant="small"
          style={{
            display: `flex`,
            cursor: `pointer`,
            userSelect: `none`,
            fontWeight: 400,
            width: `auto`,
            whiteSpace: `nowrap`,
          }}
        >
          {text}
        </Text>
      </Grid>
    );
  };

  const renderConstraints = () => (
    <>
      <ConstraintChip
        style={{
          background: RoutalPalette.neutral02,
          userSelect: `none`,
        }}
        label={
          <Grid display="flex">
            <StopsIcon height="10px" width="10px" style={{ marginRight: `4px` }} />
            <Text
              variant="label"
              style={{
                cursor: `pointer`,
                color: RoutalPalette.secondary20,
                fontSize: `10px`,
              }}
            >{`${driver.stops.length}`}</Text>
          </Grid>
        }
      />
      {activeProject ? (
        <ConstraintChip
          style={{
            background: RoutalPalette.neutral02,
            userSelect: `none`,
          }}
          label={
            <Grid display="flex">
              <Text
                variant="label"
                style={{
                  cursor: `pointer`,
                  color: RoutalPalette.secondary20,
                  fontSize: `10px`,
                }}
              >{`${driver.getTotalDistanceWithUnits(activeProject)}`}</Text>
            </Grid>
          }
        />
      ) : null}
      {activeProject?.constraints.weight && driver.max_weight ? (
        <ProgressConstraintChip
          progressPercentage={driverWeight.percentage}
          style={{
            background: RoutalPalette.neutral02,
            userSelect: `none`,
          }}
          label={
            <Text
              variant="label"
              style={{
                cursor: `pointer`,
                fontSize: `10px`,
                ...(driverWeight.percentage > 100
                  ? { color: RoutalPalette.error.medium }
                  : { color: RoutalPalette.secondary20 }),
              }}
            >{`${Math.floor(driverWeight.total)}/${
              Math.floor(driver.max_weight) !== driver.max_weight ? driver.max_weight.toFixed(2) : driver.max_weight
            }${activeProject?.units?.weight}`}</Text>
          }
        />
      ) : null}
      {activeProject?.constraints.volume && driver.max_volume ? (
        <ProgressConstraintChip
          progressPercentage={driverVolume.percentage}
          style={{
            background: RoutalPalette.neutral02,
            userSelect: `none`,
          }}
          label={
            <Text
              variant="label"
              style={{
                cursor: `pointer`,
                fontSize: `10px`,
                ...(driverVolume.percentage > 100
                  ? { color: RoutalPalette.error.medium }
                  : { color: RoutalPalette.secondary20 }),
              }}
            >{`${Math.floor(driverVolume.total)}/${
              Math.floor(driver.max_volume) !== driver.max_volume ? driver.max_volume.toFixed(2) : driver.max_volume
            }${activeProject?.units?.volume}`}</Text>
          }
        />
      ) : null}
      {driver.time_window ? (
        <ProgressConstraintChip
          progressPercentage={getTimeWindowsPercent()}
          style={{
            ...(driverTimewindowIsInvalid(driver)
              ? { background: RoutalPalette.error.medium }
              : {
                  background: RoutalPalette.neutral02,
                }),
          }}
          label={
            <Text
              variant="label"
              style={{
                cursor: `pointer`,
                fontSize: `10px`,
                ...(driverTimewindowIsInvalid(driver)
                  ? { color: RoutalPalette.neutral00 }
                  : { color: RoutalPalette.secondary20 }),
                userSelect: `none`,
              }}
            >
              {numericToText(driver.time_window)}
            </Text>
          }
        />
      ) : null}
    </>
  );

  const clickAction = () => {
    toggleExpanded();
  };

  const doubleClickAction = () => {
    setRightPanelInfo({
      detailType: `driver`,
      detailId: `${driver.id}`,
      detailTab: null,
    });
  };

  let cancelClick = false;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handlClick = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    // Click
    if (event.detail === 1) {
      // give a bit of room for double click to happen
      setTimeout(() => {
        // if not canceled, trigger click
        if (!cancelClick) {
          clickAction();
        }
      }, 250);
    }

    // DoubleClick
    if (event.detail === 2) {
      // if doubleClick, cancel single click
      cancelClick = true;

      // it clears the timeout if it exists
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // it clears the cancelClick on timeout
      timeoutRef.current = setTimeout(() => {
        cancelClick = false;
      }, 1000);

      // trigger doubleclick
      doubleClickAction();
    }
  };

  return (
    <>
      <DriverChipContainer
        container
        isFirstInLevel={isFirstInLevel}
        direction="row"
        wrap="nowrap"
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setContextAnchorDriverChip({
            screenPosition: {
              top: e.clientY,
              left: e.clientX,
            },
          });
        }}
        onClick={(event) => {
          handlClick(event);
        }}
      >
        <DriverColorBar color={HexColor.create(getRouteColor(driver, planDrivers ?? []))} />

        <Grid display="flex" flexDirection="column" justifyContent="space-evenly" flexGrow={1} padding={`0 8px`}>
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            wrap="nowrap"
            height="20px"
          >
            <Grid container direction="row" alignItems="center" wrap="nowrap">
              <RoutalTooltip
                mouseEnterDelay={0.5}
                mouseLeaveDelay={0}
                id={driver.id}
                title={driver.label ?? `No name`}
                placement="top"
              >
                {(driver.label ?? ``) !== `` ? (
                  <Text
                    variant="body"
                    style={{
                      fontWeight: 400,
                      cursor: `pointer`,
                      maxWidth: `180px`,
                      textOverflow: `ellipsis`,
                      overflow: `hidden`,
                      whiteSpace: `nowrap`,
                      color: RoutalPalette.primary80,
                      userSelect: `none`,
                    }}
                  >
                    {driver.label}
                  </Text>
                ) : (
                  <Text
                    variant="body"
                    intlId="no_name"
                    style={{ color: RoutalPalette.neutral20, fontWeight: 400, cursor: `pointer` }}
                  />
                )}
              </RoutalTooltip>
            </Grid>
            {renderLock()}

            {/* {driver.status !== `not_started` ? (
              <RoutalTooltip
                mouseEnterDelay={0.5}
                mouseLeaveDelay={0}
                id={`${driver.id}-tracing-percent`}
                title={intl.formatMessage({ id: `plans.plan.live.header.route.tracing_percent` })}
                placement="top"
              >
                <Grid
                  item
                  container
                  direction="row"
                  wrap="nowrap"
                  alignItems="center"
                  style={{ width: `unset`, marginRight: `4px` }}
                >
                  <LiveIcon style={{ color: `#393939`, height: `14px` }} />
                  <Text variant="label" style={{ color: `#393939` }}>
                    {(driver.tracing_percent ? driver.tracing_percent * 100 : 0).toFixed(0)}%
                  </Text>
                </Grid>
              </RoutalTooltip>
            ) : null} */}

            {renderHours()}
          </Grid>
          <Grid container alignItems="center" height="16px">
            {renderConstraints()}
          </Grid>
          <div style={{ height: `3px` }} />
          <Grid container alignItems="center" height="5px">
            <DriverRouteProgress driver={driver} />
          </Grid>
        </Grid>

        <Grid
          item
          style={{
            height: `100%`,
            minWidth: `16px`,
            alignContent: `center`,
          }}
        >
          {isExpanded ? (
            <RightArrowIcon
              style={{
                color: RoutalPalette.secondary60,
                transform: `rotate(90deg)`,
              }}
            />
          ) : (
            <RightArrowIcon
              style={{
                color: RoutalPalette.secondary60,
              }}
            />
          )}
        </Grid>
        <Grid
          item
          style={{
            position: `absolute`,
            right: `4px`,
            top: `4px`,
            width: `24px`,
            height: `24px`,
            textAlign: `right`,
          }}
        >
          <ProblemsTooltip type="driver" driver={driver} tooltipPlacement="right">
            <ErrorIcon
              style={{
                color: RoutalPalette.warning.dark,
                fontSize: `16px`,
                backgroundColor: RoutalPalette.neutral00,
                borderRadius: `50%`,
              }}
            />
          </ProblemsTooltip>
        </Grid>
      </DriverChipContainer>

      {contextAnchorDriverChip && (
        <PlanDriversActionsMenu
          type="contextual"
          setContextAnchor={setContextAnchorDriverChip}
          contextAnchor={{
            screenPosition: contextAnchorDriverChip.screenPosition,
          }}
          drivers={[driver]}
        />
      )}
    </>
  );
});
